// src/pages/SupportYourTownPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../lib/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import BottomNav from '../components/layout/BottomNav';
import { HeaderAvatar } from '../components/shared/HeaderAvatar';
import { useAuth } from '../lib/AuthContext';
import { callGetReferralCount, callCreateSupportCheckoutSession } from '../lib/firebase';
import toast from 'react-hot-toast';

const SupportYourTownPage = () => {
  const navigate = useNavigate();
  const [journey, setJourney] = useState(null);
  const [townName, setTownName] = useState('your town');
  const [counts, setCounts] = useState({ founding: 0, gold: 0, supporter: 0 });
  const [supporterAmount, setSupporterAmount] = useState(5);
  const [supporterMonthly, setSupporterMonthly] = useState(true);
  const [partnerMonthly, setPartnerMonthly] = useState(true);
  const [step, setStep] = useState('choose');
  const [selectedTier, setSelectedTier] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', website: '' });
  const { user } = useAuth();
  const [referralCount, setReferralCount] = useState(null);
  const [payingLoading, setPayingLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    callGetReferralCount().then(res => setReferralCount(res.data.count)).catch(() => {});
  }, [user]);

  const shareReferral = async () => {
    const link = user
      ? `https://gatheryourcommunity.com/?ref=${user.uid}`
      : 'https://gatheryourcommunity.com';
    const shareText = `Join me on Gather — the free community noticeboard for ${townName}. Everything happening locally, in one place: ${link}`;
    if (navigator.share) {
      try { await navigator.share({ text: shareText }); } catch {}
    } else {
      await navigator.clipboard.writeText(link);
      toast.success('Link copied!');
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');
    if (paymentStatus === 'success') {
      toast.success('Thank you — your support means a lot! 💚', { duration: 5000 });
      window.history.replaceState({}, '', '/support-your-town');
    } else if (paymentStatus === 'cancelled') {
      toast('No payment was taken — feel free to try again whenever suits.', { duration: 4000 });
      window.history.replaceState({}, '', '/support-your-town');
    }
  }, []);

  useEffect(() => {
    const townId = localStorage.getItem('gather-town-id') || 'loughrea';
    const stored = localStorage.getItem('gather-town-name');
    if (stored) setTownName(stored);
    getDocs(query(collection(db, 'sponsors'), where('townId', '==', townId), where('active', '==', true)))
      .then(snap => {
        const c = { founding: 0, gold: 0, supporter: 0 };
        snap.docs.forEach(d => { const t = d.data().tier; if (c[t] !== undefined) c[t]++; });
        setCounts(c);
      }).catch(() => {});
  }, []);

  return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh', paddingBottom: 'calc(var(--nav-height) + 16px)' }}>
      <div style={{ padding: '52px 20px 24px' }}>
        <button onClick={() => { if (journey || step !== 'choose') { setJourney(null); setStep('choose'); setSelectedTier(null); } else navigate(-1); }} style={s.back}>
          <div style={s.backCircle}>←</div>
          <span style={s.backLbl}>Back</span>
        </button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={s.h1}>Support {townName}</h1>
            <p style={s.sub}>Gather is free for clubs, charities and residents. Your support keeps it that way.</p>
          </div>
          <HeaderAvatar />
        </div>
      </div>

      <div style={{ padding: '0 20px' }}>

        {/* Journey choice */}
        {!journey && (
          <>
            <div style={s.sectionLabel}>I want to support as a...</div>
            <button onClick={() => setJourney('individual')} style={s.journeyBtn}>
              <span style={{ fontSize: 26 }}>❤️</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink)' }}>Resident or individual</div>
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>From €3 — any amount, any time</div>
              </div>
              <span style={{ color: 'var(--muted)', fontSize: 20 }}>›</span>
            </button>
            <button onClick={() => setJourney('business')} style={s.journeyBtn}>
              <span style={{ fontSize: 26 }}>🤝</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink)' }}>Business or organisation</div>
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>Community Partner from €50/month</div>
              </div>
              <span style={{ color: 'var(--muted)', fontSize: 20 }}>›</span>
            </button>
            <button onClick={() => setJourney('champion')} style={{ ...s.journeyBtn, background: '#2C342C', border: '2px solid #2C342C' }}>
              <span style={{ fontSize: 26 }}>⭐</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: 'white' }}>Town Champion 2026</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>Annual partnership · One per town · Get in touch</div>
              </div>
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 20 }}>›</span>
            </button>
          </>
        )}

        {/* INDIVIDUAL — slider */}
        {journey === 'individual' && step === 'choose' && (
          <div style={s.card}>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink)', marginBottom: 6 }}>Community Supporter</div>
            <div style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 24, lineHeight: 1.5 }}>
              Every bit helps keep Gather free for clubs, charities and community groups who couldn't afford to pay.
            </div>

            {/* Amount slider */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '1px' }}>Your amount</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--sage-deep)' }}>€{supporterAmount}</div>
              </div>
              <input type="range" min="3" max="100" value={Math.min(supporterAmount, 100)} onChange={e => setSupporterAmount(Number(e.target.value))}
                style={{ width: '100%', accentColor: 'var(--sage-deep)' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--muted)', marginTop: 4, marginBottom: 12 }}>
                <span>€3 minimum</span>
                <span>€100</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>Or enter an exact amount:</span>
                <input type="number" min="3" max="10000" step="1" value={supporterAmount}
                  onChange={e => {
                    const v = Math.max(3, Math.min(10000, Number(e.target.value) || 0));
                    setSupporterAmount(v);
                  }}
                  style={{ width: 90, padding: '6px 10px', borderRadius: 8, border: '1.5px solid #C8D9C9', fontSize: 14, fontWeight: 700, fontFamily: 'Nunito, sans-serif', color: 'var(--sage-deep)' }} />
              </div>
            </div>

            {/* Monthly / one-off */}
            <div style={s.toggleRow}>
              <button onClick={() => setSupporterMonthly(true)} style={{ ...s.toggleBtn, background: supporterMonthly ? 'var(--sage-deep)' : 'var(--white)', color: supporterMonthly ? 'white' : 'var(--ink)' }}>Monthly</button>
              <button onClick={() => setSupporterMonthly(false)} style={{ ...s.toggleBtn, background: !supporterMonthly ? 'var(--sage-deep)' : 'var(--white)', color: !supporterMonthly ? 'white' : 'var(--ink)' }}>One-off</button>
            </div>

            <div style={{ background: 'var(--sage-pale)', borderRadius: 12, padding: '12px 14px', marginBottom: 20, fontSize: 13, color: 'var(--sage-dark)', fontWeight: 600 }}>
              €{supporterAmount} {supporterMonthly ? 'per month · cancel any time' : 'one-off payment'}
            </div>

            <button onClick={() => { setSelectedTier('supporter'); setStep('details'); }} style={s.ctaBtn}>
              Support {townName} — €{supporterAmount}{supporterMonthly ? '/month' : ''} →
            </button>
          </div>
        )}

        {/* BUSINESS — fixed tiers */}
        {journey === 'business' && step === 'choose' && (
          <div style={s.card}>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink)', marginBottom: 16 }}>Community Partner</div>

            <div style={s.toggleRow}>
              <button onClick={() => setPartnerMonthly(true)} style={{ ...s.toggleBtn, background: partnerMonthly ? 'var(--sage-deep)' : 'var(--white)', color: partnerMonthly ? 'white' : 'var(--ink)' }}>Monthly</button>
              <button onClick={() => setPartnerMonthly(false)} style={{ ...s.toggleBtn, background: !partnerMonthly ? 'var(--sage-deep)' : 'var(--white)', color: !partnerMonthly ? 'white' : 'var(--ink)' }}>One-off</button>
            </div>

            <div style={{ background: '#FDF3E0', border: '1.5px solid #E8C870', borderRadius: 16, padding: '18px 16px', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#7A5010' }}>🤝 Community Partner</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#7A5010' }}>{partnerMonthly ? '€50/mo' : '€75'}</div>
              </div>
              <div style={{ fontSize: 13, color: '#9A7030', lineHeight: 1.5 }}>
                Logo and business profile on the {townName} page. Recognised as a business investing in your community. Up to 3 partners per town.
              </div>
            </div>

            <button onClick={() => { setSelectedTier('gold'); setStep('details'); }} style={s.ctaBtn}>
              Become a Community Partner →
            </button>
          </div>
        )}

        {/* TOWN CHAMPION — get in touch */}
        {journey === 'champion' && (
          <div style={{ ...s.card, background: '#2C342C', border: 'none' }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'white', marginBottom: 8 }}>⭐ Town Champion 2026</div>
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, marginBottom: 20 }}>
              One exclusive annual partnership per town. The Town Champion is the organisation making Gather possible in {townName} — recognised across all events and pages throughout the year.
            </div>
            <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 12, padding: '14px 16px', marginBottom: 20 }}>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginBottom: 4 }}>Annual partnership · 2026 · {townName}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'white' }}> <span style={{ fontSize: 14, fontWeight: 500 }}></span></div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}></div>
            </div>
            <button onClick={() => window.open(`mailto:hello@gatheryourcommunity.com?subject=Town Champion 2026 — ${townName}&body=Hi, I'm interested in becoming the Town Champion for ${townName} on Gather.`, '_blank')}
              style={{ ...s.ctaBtn, background: 'white', color: '#2C342C' }}>
              Get in touch →
            </button>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', textAlign: 'center', marginTop: 12 }}>
              We'll respond within 24 hours to discuss the partnership.
            </p>
          </div>
        )}

        {/* Details form */}
        {step === 'details' && (
          <div style={s.card}>
            <div style={s.formTitle}>Your details</div>
            <div style={s.field}><div style={s.label}>Name *</div>
              <input style={s.input} value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                placeholder={journey === 'business' ? 'Business name' : 'Your name'} /></div>
            <div style={s.field}><div style={s.label}>Email *</div>
              <input style={s.input} type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
            {journey === 'business' && <div style={s.field}><div style={s.label}>Website (optional)</div>
              <input style={s.input} value={form.website} onChange={e => setForm({...form, website: e.target.value})} placeholder="https://..." /></div>}
            <button onClick={() => {
              if (!form.name || !form.email) { alert('Please fill in name and email'); return; }
              setStep('confirm');
            }} style={s.ctaBtn}>Continue to payment →</button>
          </div>
        )}

        {/* Confirm */}
        {step === 'confirm' && (
          <div style={s.card}>
            <div style={s.formTitle}>Almost there</div>
            <p style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 20, lineHeight: 1.6 }}>
              You'll be taken to a secure Stripe payment page to complete your {
                selectedTier === 'supporter'
                  ? `${supporterMonthly ? 'monthly' : 'one-off'} support of €${supporterAmount}`
                  : `${partnerMonthly ? 'monthly' : 'one-off'} Community Partner payment`
              }.
            </p>
            <button
              onClick={async () => {
                const recurring = selectedTier === 'supporter' ? supporterMonthly : partnerMonthly;
                const amount = selectedTier === 'supporter' ? supporterAmount : (partnerMonthly ? 50 : 75);
                const tierLabel = selectedTier === 'supporter' ? 'Community Supporter' : 'Community Partner';
                setPayingLoading(true);
                try {
                  const townId = localStorage.getItem('gather-town-id') || 'loughrea';
                  const res = await callCreateSupportCheckoutSession({
                    amount, recurring, tierLabel,
                    name: form.name, email: form.email,
                    townId, townName,
                  });
                  window.location.href = res.data.url;
                } catch (err) {
                  console.error(err);
                  toast.error('Something went wrong starting payment — please try again or email us below');
                  setPayingLoading(false);
                }
              }}
              disabled={payingLoading}
              style={{ ...s.ctaBtn, opacity: payingLoading ? 0.7 : 1 }}
            >
              {payingLoading ? 'Taking you to payment...' : 'Continue to secure payment →'}
            </button>
            <p style={{ fontSize: 12.5, color: 'var(--muted)', textAlign: 'center', marginTop: 14, lineHeight: 1.5 }}>
              Prefer not to pay online? Email us at{' '}
              <a href="mailto:hello@gatheryourcommunity.com" style={{ color: 'var(--sage-deep)', fontWeight: 700 }}>hello@gatheryourcommunity.com</a>
              {' '}and we'll sort it another way.
            </p>
          </div>
        )}

        {step === 'choose' && (
          <div style={{ background: 'var(--sage-pale)', borderRadius: 16, padding: 18, marginTop: 20 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 }}>💌 Refer a friend</div>
            <div style={{ fontSize: 13, color: 'var(--sage-dark)', fontWeight: 500, marginBottom: 12, lineHeight: 1.5 }}>
              Sharing Gather is one of the best ways to support {townName} — no payment needed.
              {user && referralCount !== null && referralCount > 0 && (
                <span> <strong>{referralCount} {referralCount === 1 ? 'person has' : 'people have'}</strong> joined through your invite so far.</span>
              )}
              {!user && (
                <span> Sign in to get your own personal link and track who joins.</span>
              )}
            </div>
            <button
              onClick={shareReferral}
              style={{ width: '100%', background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 12, padding: '12px 14px', fontSize: 13.5, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}
            >
              🔗 Share Gather
            </button>
          </div>
        )}

        {step === 'choose' && (
          <button
            onClick={() => navigate('/become-town-admin')}
            style={{ width: '100%', background: 'var(--white)', border: '1.5px solid var(--border)', borderRadius: 16, padding: '16px', marginTop: 12, display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', fontFamily: 'Nunito, sans-serif', textAlign: 'left', boxShadow: 'var(--shadow-sm)' }}
          >
            <span style={{ fontSize: 26 }}>🙋</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink)' }}>Want to help manage {townName}?</div>
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>Apply to become a Town Admin — no payment involved</div>
            </div>
            <span style={{ color: 'var(--muted)', fontSize: 20 }}>›</span>
          </button>
        )}

        <div style={s.missionFooter}>
          <div style={s.missionText}></div>
        </div>
      </div>
      <BottomNav />
    </div>
  );
};

const s = {
  back: { display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 16 },
  backCircle: { width: 34, height: 34, background: 'var(--white)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: '#2C4A2E', boxShadow: 'var(--shadow-sm)' },
  backLbl: { fontSize: 13, color: '#4A6A4A', fontWeight: 600 },
  h1: { fontSize: 26, fontWeight: 800, color: '#1A2E1A', marginBottom: 8 },
  sub: { fontSize: 14, color: '#4A6A4A', lineHeight: 1.6 },
  proofCard: { background: 'var(--white)', borderRadius: 16, padding: '14px 16px', marginBottom: 20, boxShadow: 'var(--shadow-sm)' },
  proofTitle: { fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '1px' },
  proofStat: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  proofNum: { fontSize: 22, fontWeight: 800, color: 'var(--sage-deep)' },
  proofLbl: { fontSize: 10, color: 'var(--muted)', fontWeight: 600, textAlign: 'center' },
  sectionLabel: { fontSize: 12, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 12 },
  journeyBtn: { width: '100%', background: 'var(--white)', border: '1.5px solid var(--border)', borderRadius: 16, padding: '16px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', fontFamily: 'Nunito, sans-serif', textAlign: 'left', boxShadow: 'var(--shadow-sm)' },
  card: { background: 'var(--white)', borderRadius: 18, padding: 20, marginBottom: 16, boxShadow: 'var(--shadow-sm)' },
  toggleRow: { display: 'flex', background: 'var(--cream)', borderRadius: 12, padding: 4, marginBottom: 16 },
  toggleBtn: { flex: 1, border: 'none', borderRadius: 10, padding: '10px', fontSize: 14, fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer' },
  ctaBtn: { width: '100%', background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 16, padding: 17, fontSize: 16, fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer', marginTop: 8 },
  formTitle: { fontSize: 18, fontWeight: 800, color: 'var(--ink)', marginBottom: 16 },
  field: { marginBottom: 14 },
  label: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', marginBottom: 6 },
  input: { width: '100%', padding: '12px 14px', borderRadius: 12, border: '1.5px solid #C8D9C9', fontSize: 15, fontFamily: 'Nunito, sans-serif', color: 'var(--ink)', background: 'var(--white)', boxSizing: 'border-box', outline: 'none' },
  missionFooter: { marginTop: 32, padding: 16, background: 'var(--sage-pale)', borderRadius: 14, textAlign: 'center' },
  missionText: { fontSize: 13, color: '#4A6A4A', lineHeight: 1.6 },
};

export default SupportYourTownPage;
